/**
 * StatusChip — the ONE reusable status/label pill for the whole web product.
 *
 * Why this exists
 * ───────────────
 * The inspection list grew a modern, low-colour "soft chip" (a coloured dot +
 * a faint tint + a subtle border + readable coloured text). It reads far nicer
 * than the heavy, saturated AntD `<Tag>` pills scattered across other pages.
 * This component generalises that look so every list, card, and detail header
 * shows the SAME chip — modern, calm, readable, and consistent — instead of
 * each page re-inventing its own colours.
 *
 * When you need a status pill, a type label, or a small badge on ANY page:
 * reach for `<StatusChip>` (or the `.sig-chip` CSS classes it rides — see
 * `index.css`). Do NOT hand-roll a `<Tag color="...">` or an inline-styled
 * `<span>` — that is how the palette drifts.
 *
 * The tone palette (semantic — pick by MEANING, never by "what colour looks
 * nice"). Each tone is a tint bg + coloured text + a matching dot & border.
 * Mirrors the design system §2.3 status pairs (`specification/design-system.md`):
 *
 *   tone        colour   use for
 *   ─────────   ──────   ────────────────────────────────────────────────────
 *   info        blue     Scheduled, upcoming, informational / neutral-positive
 *   progress    teal     ACTIVE / in-progress / working / running (NOT a warning)
 *   ok          green    Completed, verified, resolved, synced, on-track, paid
 *   warn        amber    Needs attention, pending, due-soon, at-risk, overdue-soon
 *   danger      red      Failed, error, expired, breached, rejected, critical
 *   brand       violet   Special/highlight state (campaign, reviewed, AI, featured)
 *   neutral     grey     Cancelled, archived, draft, inert, N/A
 *   type        outline  A CATEGORY/type LABEL, not a state (white pill, no dot)
 *
 * Key rule: "In progress / active" is `progress` (teal), never `warn` (amber).
 * Amber means "something needs a human"; teal means "it's running, all good".
 *
 * Usage
 * ─────
 *   <StatusChip tone="progress" label="In progress" />
 *   <StatusChip tone="ok" label={t('common:status.completed')} />
 *   <StatusChip tone="type" label="Move-Out" />          // type label, no dot
 *   <StatusChip tone={commonStatusTone(wo.status)} label={statusLabel} />
 *
 * Map an enum → tone with `commonStatusTone()` (covers the usual WO / inspection
 * / invoice / sync statuses). For a domain enum it doesn't know, keep a tiny
 * `Record<Status, ChipTone>` next to that page's status labels and pass `tone`
 * explicitly — the tone vocabulary is the shared contract, the mapping is local.
 */
import { forwardRef, type CSSProperties, type HTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';

export type ChipTone =
  | 'info'
  | 'progress'
  | 'ok'
  | 'warn'
  | 'danger'
  | 'brand'
  | 'neutral'
  | 'type';

interface StatusChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'onClick'> {
  /** Semantic tone — pick by meaning (see the table in the file header). */
  tone: ChipTone;
  /** Visible text. Always pass a localized, human label — never a raw enum. */
  label: ReactNode;
  /** Show the leading status dot. Forced off for `type` (a label, not a state). */
  dot?: boolean;
  /** Compact size for dense metric contexts (KPI cards, analytics tiles). */
  size?: 'sm';
  /**
   * Optional leading glyph shown IN PLACE of the dot (e.g. a warning triangle
   * on a `warn`/`danger` chip). Inherits the tone's text colour. Use sparingly
   * — the dot is the default; reach for an icon only when it adds real signal.
   */
  icon?: ReactNode;
  /** Makes the chip interactive (e.g. a click-to-toggle status). When set, the
   *  chip gets `cursor: pointer` and Enter/Space keyboard activation. Receives
   *  the click event (use `e.stopPropagation()` inside a clickable row). */
  onClick?: (e: ReactMouseEvent<HTMLSpanElement>) => void;
  /**
   * DECORATIVE per-CATEGORY tint (design-system §2.3a) — a CSS colour that
   * renders as a soft sig-chip tint (light bg + that colour as text + border,
   * no dot). Use ONLY to colour-code a CATEGORY/type label where the hue is a
   * scanning aid (e.g. a work-order trade: Plumbing, Painting). NEVER for
   * state — status meaning comes from `tone`. Pair with `tone="type"`.
   */
  accentColor?: string;
}

// forwardRef + DOM-prop passthrough so the chip works as a Tooltip/Popconfirm/
// Dropdown trigger (those clone the child, attach a ref, and inject hover/focus
// handlers — a plain function component would drop them).
export const StatusChip = forwardRef<HTMLSpanElement, StatusChipProps>(function StatusChip(
  { tone, label, dot = true, size, icon, className, style, onClick, accentColor, ...rest },
  ref,
) {
  const showDot = dot && !icon && !accentColor && tone !== 'type';
  const interactive = typeof onClick === 'function';
  // A category accent renders the chip in the sig-chip style from any hue.
  const accentStyle: CSSProperties | null = accentColor
    ? {
        background: `color-mix(in srgb, ${accentColor} 13%, #fff)`,
        color: accentColor,
        borderColor: `color-mix(in srgb, ${accentColor} 34%, #fff)`,
      }
    : null;
  return (
    <span
      ref={ref}
      {...rest}
      className={`sig-chip sig-chip--${tone}${size === 'sm' ? ' sig-chip--sm' : ''}${className ? ` ${className}` : ''}`}
      style={{
        ...(interactive ? { cursor: 'pointer' } : null),
        ...accentStyle,
        ...style,
      }}
      onClick={onClick}
      {...(interactive
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onKeyDown: (e: ReactKeyboardEvent<HTMLSpanElement>) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); }
            },
          }
        : {})}
    >
      {icon != null && <span className="sig-chip__icon">{icon}</span>}
      {showDot && <span className="sig-chip__dot" />}
      {label}
    </span>
  );
});

/**
 * Best-effort enum → tone map for the statuses that recur across the product.
 * Case-insensitive; accepts the common spellings/synonyms. Returns `neutral`
 * for anything unknown so a new enum value degrades gracefully (grey pill)
 * instead of throwing or mis-colouring.
 *
 * If a page has a domain-specific enum this doesn't cover, DON'T extend this
 * with one-off values — keep a small local `Record<Status, ChipTone>` and pass
 * `tone` explicitly. This helper is only for the genuinely shared vocabulary.
 */
export function commonStatusTone(status: string | null | undefined): ChipTone {
  if (!status) return 'neutral';
  switch (status.toUpperCase()) {
    // Active / in-flight — teal, never amber.
    case 'IN_PROGRESS':
    case 'ACTIVE':
    case 'RUNNING':
    case 'PROCESSING':
    case 'DISPATCHED':
    case 'IN_REVIEW':
      return 'progress';

    // Upcoming / informational.
    case 'SCHEDULED':
    case 'OPEN':
    case 'NEW':
    case 'PENDING_SCHEDULE':
    case 'DRAFT_SCHEDULED':
      return 'info';

    // Done / good.
    case 'COMPLETED':
    case 'COMPLETE':
    case 'DONE':
    case 'RESOLVED':
    case 'VERIFIED':
    case 'VERIFIED_CLOSED':
    case 'CLOSED':
    case 'PAID':
    case 'APPROVED':
    case 'SYNCED':
    case 'ACTIVE_OK':
      return 'ok';

    // Needs a human.
    case 'PENDING':
    case 'PENDING_APPROVAL':
    case 'PENDING_REVIEW':
    case 'AWAITING':
    case 'ON_HOLD':
    case 'AT_RISK':
    case 'DUE_SOON':
    case 'NEEDS_ATTENTION':
    case 'DISPUTED':
      return 'warn';

    // Bad / terminal-negative.
    case 'FAILED':
    case 'ERROR':
    case 'EXPIRED':
    case 'BREACHED':
    case 'OVERDUE':
    case 'REJECTED':
    case 'DECLINED':
    case 'CRITICAL':
      return 'danger';

    // Special / highlight.
    case 'REVIEWED':
    case 'CAMPAIGN':
    case 'FEATURED':
      return 'brand';

    // Inert.
    case 'CANCELLED':
    case 'CANCELED':
    case 'ARCHIVED':
    case 'DRAFT':
    case 'INACTIVE':
    case 'NA':
    case 'N/A':
      return 'neutral';

    default:
      return 'neutral';
  }
}
